import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  SvgIcon,
  Rating,
  styled
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';

import ic_callofduty from '../static/img/gameIcons/ic_callofduty.png';
import ic_valorant from '../static/img/gameIcons/ic_valorant.png';
import ic_pubg from '../static/img/gameIcons/ic_pubg.png';
import ic_fortnite from '../static/img/gameIcons/ic_fortnite.png';
import ic_freefire from '../static/img/gameIcons/ic_freefire.png';
import ic_minecraft from '../static/img/gameIcons/ic_minecraft.png';


const GameIcon = styled('img')(({ theme }) => ({
  width: '32px',
  height: 'auto',
  borderRadius: '6px',
  marginRight: '12px',
}));

const ArticleContainer = styled(Box)(({ theme, isReview }) => ({
  backgroundColor: '#252525',
  borderRadius: '12px',
  padding: '24px',
  margin: '16px 0',
  border: 'none',
  borderTop: isReview ? 'none' : '6px solid #FAAE1C',
}));

const GameTitleHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '16px',
  backgroundColor: 'rgb(55, 55, 55)',
  borderTopLeftRadius: '8px',
  borderTopRightRadius: '8px',
  borderBottomLeftRadius: '8px',
  borderBottomRightRadius: '8px',
  marginBottom: '24px',
  position: 'relative',
}));

const ReviewsButton = styled(Box)(({ theme }) => ({
  padding: '16px 20px',
  backgroundColor: '#FAAE1C',
  borderRadius: '8px',
  marginBottom: '32px',
  textAlign: 'center',
  borderTop: '3px solid #FFFFFF',
}));

const ReviewsContainer = styled(Box)(({ theme }) => ({
  marginBottom: '24px',
  position: 'relative',
}));

function ArticleView() {
  const theme = useTheme();
  const isDesktop = useMediaQuery('(min-width:900px)');
  const navigate = useNavigate();
  const location = useLocation();
  
  const articleData = location.state?.article;
  const gameName = location.state?.gameName || 'Call of Duty';

  const gameIcons = {
    'Call of Duty': ic_callofduty,
    'Valorant': ic_valorant,
    'PUBG': ic_pubg,
    'Fortnite': ic_fortnite,
    'Freefire': ic_freefire,
    'Minecraft': ic_minecraft,
  };

  useEffect(() => {
    if (!articleData) {
      navigate('/news');
      return;
    }
    
    window.scrollTo(0, 0);
    
    document.title = `${articleData.title} | Gamez Academy`;
  }, [articleData, navigate]);

  if (!articleData) {
    return null;
  }

  return (
    <Box
      sx={{
        width: '100%',
        padding: isDesktop ? '20px' : '16px',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        scrollbarWidth: 'none',
        backgroundColor: theme.palette.background.default,
        minHeight: '100vh'
      }}
    >
        <ArticleContainer isReview={articleData.type === 'review'}>
          {articleData.type === 'review' ? (
            <ReviewsContainer>
              <ReviewsButton>
                <Typography
                  sx={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#000000',
                    textTransform: 'capitalize'
                  }}
                >
                  {gameName} Reviews
                </Typography>
              </ReviewsButton>
              <Box 
                sx={{ 
                  position: 'absolute',
                  bottom: '-28px',
                  right: '8px',
                  backgroundColor: '#2A2A2A',
                  borderRadius: '20px',
                  padding: '8px 8px',
                  zIndex: 10
                }}
              >
                <Rating
                  value={articleData.rating || 0}
                  precision={0.5}
                  readOnly
                  size="small"
                  sx={{
                    '& .MuiRating-iconFilled': {
                      color: '#FFD700',
                    },
                    '& .MuiRating-iconEmpty': {
                      color: 'rgba(255, 255, 255, 0.3)',
                    }
                  }}
                />
              </Box>
            </ReviewsContainer>
          ) : (
            <GameTitleHeader>
              <Typography
                sx={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#F2BC1A',
                  lineHeight: '24px',
                  textTransform: 'capitalize',
                  flex: 1,
                  textAlign: 'center'
                }}
              >
                {gameName} {articleData.type}
              </Typography>
              <GameIcon 
                src={gameIcons[gameName]} 
                alt={gameName}
              />
            </GameTitleHeader>
          )}
          
          <Typography
            variant="h4"
            sx={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: '24px',
              fontWeight: '600',
              color: '#FFFFFF',
              lineHeight: '1.3',
              marginBottom: '16px'
            }}
          >
            {articleData.title}
          </Typography>
          
          {articleData.image && (
            <Box
              component="img"
              src={articleData.image}
              alt={articleData.title}
              sx={{
                width: '100%',
                maxHeight: '300px',
                borderRadius: '8px',
                objectFit: 'cover',
                marginBottom: '24px'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          )}
          
          <Typography
            sx={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: '16px',
              fontWeight: '400',
              color: '#D9D9D9',
              lineHeight: '1.6',
              '& div': {
                marginBottom: '16px',
              },
              '& div:empty': {
                marginBottom: '8px',
              },
              '& b, & strong': {
                fontWeight: '600',
                color: '#FAAE1C',
                fontSize: '18px',
                display: 'block',
                marginBottom: '12px',
                marginTop: '20px',
              },
              '& h1, & h2, & h3, & h4, & h5, & h6': {
                fontFamily: "'Poppins', sans-serif",
                fontWeight: '600',
                color: '#FAAE1C',
                marginBottom: '12px',
                marginTop: '20px',
              },
              '& h1': { fontSize: '24px' },
              '& h2': { fontSize: '22px' },
              '& h3': { fontSize: '20px' },
              '& h4': { fontSize: '18px' },
              '& p': {
                marginBottom: '16px',
                lineHeight: '1.6',
              },
              '& ul, & ol': {
                paddingLeft: '20px',
                marginBottom: '16px',
                '& li': {
                  marginBottom: '8px',
                  lineHeight: '1.5',
                }
              },
              '& a': {
                color: '#FAAE1C',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                }
              },
              '& blockquote': {
                borderLeft: '4px solid #FAAE1C',
                paddingLeft: '16px',
                marginLeft: '0',
                marginBottom: '16px',
                fontStyle: 'italic',
                color: '#F7F7F7',
              },
              '& br': {
                lineHeight: '0.5',
                marginBottom: '4px',
              },
              '& div:has(br:only-child)': {
                marginBottom: '8px',
              }
            }}
            dangerouslySetInnerHTML={{ __html: articleData.content }}
          />
          
          {articleData.tags && articleData.tags.length > 0 && (
            <Box sx={{ marginTop: '24px' }}>
              <Typography
                sx={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#FFFFFF',
                  marginBottom: '12px'
                }}
              >
                Tags:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {articleData.tags.map((tag, index) => (
                  <Box
                    key={index}
                    sx={{
                      backgroundColor: '#3A3A3A',
                      padding: '4px 12px',
                      borderRadius: '16px',
                      fontSize: '12px',
                      color: '#D9D9D9'
                    }}
                  >
                    {tag}
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </ArticleContainer>
    </Box>
  );
}

export default ArticleView;